import { setHours, setMinutes, format, parse } from "date-fns";
import React, { useState, useEffect, useRef } from "react";

const generateTimeOptions = (interval = 15) => {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += interval) {
      const time = setHours(setMinutes(new Date(), min), hour);
      times.push({
        label: format(time, 'hh:mm aa'),
        value: time,
      });
    }
  }
  return times;
};

const timeOptions = generateTimeOptions();

export default function TimeSearchPicker({ value, onChange, placeholder, className }) {
	const [search, setSearch] = useState("");
	const [selectedTime, setSelectedTime] = useState(value || null);
	const [showDropdown, setShowDropdown] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);
	const wrapperRef = useRef(null);
	const activeItemRef = useRef(null);

	// Update on external value change
	useEffect(() => {
	if (value) {
		setSelectedTime(value);
		setSearch("");

		// Set active index based on value
		const index = timeOptions.findIndex(
		(t) => format(t.value, "hh:mm aa") === format(value, "hh:mm aa")
		);
		setActiveIndex(index >= 0 ? index : 0);
	}
	}, [value]);

	const filteredOptions = timeOptions.filter((t) =>
	t.label.toLowerCase().includes(search.toLowerCase())
	);

	const handleInputChange = (e) => {
	const inputVal = e.target.value;
	setSearch(inputVal);
	setShowDropdown(true);

	// Reset active index to 0 while typing
	setActiveIndex(0);

	if (inputVal.trim() === "") {
		setSelectedTime(null);
		onChange(null);
	}
	};

	const handleSelect = (time) => {
	setSelectedTime(time.value);
	setSearch(""); // Show selected time only
	setShowDropdown(false);
	onChange(time.value);
	};

	const handleKeyDown = (e) => {
	if (!showDropdown) return;

	if (e.key === "ArrowDown") {
		e.preventDefault();
		setActiveIndex((prev) => (prev + 1) % filteredOptions.length);
	} else if (e.key === "ArrowUp") {
		e.preventDefault();
		setActiveIndex((prev) => (prev - 1 + filteredOptions.length) % filteredOptions.length);
	} else if (e.key === "Enter") {
		e.preventDefault();
		if (filteredOptions[activeIndex]) {
		handleSelect(filteredOptions[activeIndex]);
		}
	} else if (e.key === "Escape") {
		setShowDropdown(false);
	}
	};

	// Scroll to active item
	useEffect(() => {
		if (showDropdown && activeItemRef.current) {
			// Wait for the dropdown to render
			setTimeout(() => {
			activeItemRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
			}, 0);
		}
	}, [showDropdown, activeIndex]);

	// Close dropdown on outside click
	useEffect(() => {
	const handleClickOutside = (e) => {
		if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
		setShowDropdown(false);

		// Parse and accept typed time on blur
		if (search.trim()) {
			const parsed = parse(search, "hh:mm aa", new Date());
			if (!isNaN(parsed)) {
			setSelectedTime(parsed);
			onChange(parsed);
			} else {
			setSearch("");
			}
		}
		}
	};

	document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [search]);

	// Set activeIndex based on selected value when dropdown opens
	const handleFocus = () => {
	setShowDropdown(true);

	if (selectedTime) {
		const index = filteredOptions.findIndex(
			(t) => format(t.value, "hh:mm aa") === format(selectedTime, "hh:mm aa")
		);
		setActiveIndex(index >= 0 ? index : 0);
	} else {
		setActiveIndex(0);
	}
	};

	// Final input value logic
	const displayValue = search || (selectedTime ? format(selectedTime, "hh:mm aa") : "");
  return (
    <div className="relative" ref={wrapperRef}>
      <input
        type="text"
        value={displayValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || "Search or select time"}
        className={className}
        autoComplete="off"
      />

      {showDropdown && filteredOptions.length > 0 && (
        <ul className="absolute z-10 bg-white border rounded w-full max-h-48 overflow-y-auto mt-1">
          {filteredOptions.map((time, idx) => (
            <li
              key={idx}
              ref={idx === activeIndex ? activeItemRef : null}
              className={`px-3 py-2 cursor-pointer ${
                idx === activeIndex ? "bg-gray-200" : "hover:bg-gray-100"
              }`}
              onClick={() => handleSelect(time)}
            >
              {time.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
